import { useCallback, useState } from "react";
import { Group, Loader, Stack, Text, Title, Image, UnstyledButton } from "@mantine/core";
import { FormattedMessage } from "react-intl";
import type { StyleAccountAndVersion } from "@mapka/style-editor";
import { StyleChatInput } from "@mapka/style-editor";
import { captureEvent } from "../../../utils/posthog.js";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../../../trpc.js";
import { useLocale } from "../../../hooks/locale.js";
import { container, tinyMap, selected } from "./OverviewHero.module.css";
import { DEFAULT_BASEMAP_KEY } from "@project/common";
import { useNavigate } from "@tanstack/react-router";
import { Event } from "../../../constants/events.js";
import { ErrorAlert } from "./ErrorAlert.js";
import clsx from "clsx";
import osmBrightThumb from "../../../assets/osm-bright-basemap.png";
import positronThumb from "../../../assets/positron-basemap.png";
import osmLibertyThumb from "../../../assets/osm-liberty-basemap.png";

// Keyed by the stable style.key slug (matches style.key returned by the API).
// Add one entry per basemap as its asset is added at src/assets/<key>-basemap.png.
// Styles without an entry are not shown in the picker; insertion order is the picker order.
const THUMBNAILS: Record<string, string> = {
  [DEFAULT_BASEMAP_KEY]: osmLibertyThumb,
  "osm-bright-gl": osmBrightThumb,
  "positron-gl": positronThumb,
};

interface ChatWrapperProps {
  isLoading: boolean;
  isError: boolean;
  styleAndVersion: StyleAccountAndVersion | undefined;
}

function ChatWrapper({ isLoading, isError, styleAndVersion }: ChatWrapperProps) {
  const locale = useLocale();
  const navigate = useNavigate();

  const handleSendMessage = useCallback((chatId: number) => {
    if (!styleAndVersion) return;
    captureEvent(Event.OverviewStartChat);

    void navigate({
      to: "/$accountName/maps/$map/ai",
      search: { chatId },
      params: {
        accountName: styleAndVersion.account.name,
        map: styleAndVersion.style.key,
      },
    });
  }, [styleAndVersion]);

  if (isLoading) {
    return <Loader size="lg" />;
  };

  if (isError) {
    return (
      <ErrorAlert>
        <FormattedMessage defaultMessage="Error preparing chat input" id="error.StyleChatInput" />
      </ErrorAlert>
    );
  }

  if (!styleAndVersion) return null;

  return (
    <StyleChatInput
      key={styleAndVersion.style.id} // trigger remount and re-initialize store when style changes
      locale={locale}
      captureEvent={captureEvent}
      onSendMessage={handleSendMessage}
      accountName={styleAndVersion.account.name}
      style={styleAndVersion}
    />
  );
}

export function OverviewHero() {
  const trpc = useTRPC();

  const {
    data: publicStyles = [],
    isLoading,
    isError,
  } = useQuery(trpc.getPublicStylesWithVersions.queryOptions());

  const [selectedKey, setSelectedKey] = useState(DEFAULT_BASEMAP_KEY);

  // The API returns public styles unordered, so drive both the picker order and
  // the selection off the thumbnail keys rather than the response order.
  const basemaps = Object.keys(THUMBNAILS)
    .map(key => publicStyles.find(({ style }) => style.key === key))
    .filter(basemap => basemap !== undefined);

  const selectedBasemap = basemaps.find(({ style }) => style.key === selectedKey) ?? basemaps[0];

  return (
    <Stack py={64} px={12} gap={24} align="center" className={container}>
      <Stack gap={8}>
        <Title order={1} ta="center">
          <FormattedMessage defaultMessage="Create your map with AI" id="title.cta" />
        </Title>
        <Text ta="center">
          <FormattedMessage
            defaultMessage="You can ask AI to style or modify your map using natural language."
            id="text.productSummary"
          />
        </Text>
      </Stack>

      <Stack py="md" gap="md" justify="center" p={0}>
        <FormattedMessage
          defaultMessage="Select basemap"
          id="label.selectBasemap"
        />
        <Group justify="center">
          {basemaps.map(({ style, version: { spec } }) => (
            <UnstyledButton
              key={style.key}
              onClick={() => {
                setSelectedKey(style.key);
              }}
              className={clsx(
                tinyMap,
                {
                  [selected]: selectedBasemap?.style.key === style.key,
                },
              )}
            >
              <Image
                src={THUMBNAILS[style.key]}
                alt={spec.name}
                w={90}
                h={90}
              />
            </UnstyledButton>
          ))}
        </Group>
      </Stack>

      <ChatWrapper
        isError={isError}
        isLoading={isLoading}
        styleAndVersion={selectedBasemap as unknown as StyleAccountAndVersion}
      />
    </Stack>
  );
};
